import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, input, model, viewChildren } from '@angular/core';
import { DiaryLogStatus, DiaryLogType, EventLogField } from '../../core';
import { LogItem } from '../../models';
import { BaseComponent, ConvertDatePipe, DiaryMessagePipe, LogTypeLabelPipe, SortableHeaderComponent, SortEvent } from '../../shared';


/**
 * Component for events table in patient history page.
 */
@Component({
	selector: 'tsp-history-table',
	imports: [
		DatePipe,
		ConvertDatePipe,
		LogTypeLabelPipe,
		DiaryMessagePipe,
		SortableHeaderComponent
	],
	templateUrl: './history-table.component.html',
	changeDetection: ChangeDetectionStrategy.Eager
})
export class HistoryTableComponent extends BaseComponent {

	items = model.required<LogItem[] | undefined>();
	sortOrder = model.required<SortEvent>();
	isDiary = input.required<boolean>();
	isReadonly = input<boolean>();

	headers = viewChildren(SortableHeaderComponent);

	isReadCheckboxVisible = computed(() => !this.isDiary() && !this.isReadonly());
	isAllSelected = computed(() => this.items()?.every(m => m.isSelected) ?? false);

	activeColumn = computed(() => this.sortOrder().column);

	readonly LOG_TYPE = DiaryLogType;
	readonly FIELDS = EventLogField;


	protected _updateHeadersDirection = effect(() => {
		this.headers().forEach(h => h.direction.set(''));
		this.headers().find(h => h.name() === this.sortOrder().column)?.direction.set(this.sortOrder().direction);
	});

	/**
	* Update column sort order and sort list.
	*/
	onSort({ column, direction }: SortEvent) {
		this.sortOrder.set({ column, direction });
	}

	/**
	 * Select all items.
	 */
	selectAll(event: Event): void {
		const val = (event.target as HTMLInputElement).checked;
		this.items.update(list => list?.map(l => ({ ...l, isSelected: val })));
	}

	/**
	 * Select input item.
	 */
	select(item: LogItem, val: boolean): void {
		item.isSelected = val;
		this.items.update(list => list ? [...list] : undefined);
	}

	/**
	 * Get image src according to log type. 
	 */
	logTypeIcon(type: DiaryLogType): string {
		switch (type) {
			case DiaryLogType.AppStateChanged: return 'images/ico_round_w.png';
			case DiaryLogType.DailyMoodSurveyAnswered: return 'images/daily_mood_survey_ans_w.png';
			case DiaryLogType.MotivationalMessageFlag: return 'images/ico_motivational_w.png';
			case DiaryLogType.MotivationalMessageRead: return 'images/ico_motivational_w.png';
			case DiaryLogType.PersonalGoalSet: return 'images/ico_goal_w.png';
			case DiaryLogType.PinSiteCareDone: return 'images/ico_pinsitecare_w.png';
			case DiaryLogType.PrescriptionUpdated: return 'images/prescr_sync_w.png';
			case DiaryLogType.StrutAdjustmentDone: return 'images/ico_strut_log_w.png';
			case DiaryLogType.StrutAdjustmentPostponed: return 'images/ico_strut_log_w.png';
			case DiaryLogType.TreatmentRevoked: return 'images/ico_round_w.png';
			default: return '';
		}
	}

	/**
	 * Get image src according to log status.
	 */
	logStatusIcon(status: DiaryLogStatus): string {
		switch (status) {
			case DiaryLogStatus.Ok: return 'images/green-circle-48.png';
			case DiaryLogStatus.Warning: return 'images/yellow-circle-48.png';
			case DiaryLogStatus.Problem: return 'images/red-circle-48.png';
			case DiaryLogStatus.Neutral: return 'images/grey-circle-48.png';
			default: return '';
		}
	}

	/**
	 * Check if frame is visible.
	 */
	isFrameVisible(type: DiaryLogType): boolean {
		return type === DiaryLogType.StrutAdjustmentDone ||
			type === DiaryLogType.StrutAdjustmentPostponed ||
			type === DiaryLogType.PrescriptionUpdated ||
			type === DiaryLogType.TreatmentRevoked;
	}

}